import {
  AssetLocationSource,
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatTimeWithUserTimezone,
  FormType,
  GetAssetLocationsRequestDTO,
  GetAssetLocationsResponseDTO,
} from '@gearbelt/global';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { formatRateCellValue } from '@/utils/currency';

const columnHelper = createColumnHelper<GetAssetLocationsResponseDTO['data'][number]>();

export const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2">
          <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
        </label>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
        </label>
      </div>
    ),
    meta: {
      cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
    },
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor('asset', {
    header: 'Asset',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
    },
    enableSorting: true,
    enableGrouping: true,
    enableColumnFilter: true,
    getGroupingValue: (row) => (row.asset?.internalId ? EntityRefUtil.createAssetLabel(row.asset) : 'None'),
    meta: {
      columnGroup: ASSET_SECTION_LABEL,
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data } = await (await ApiService.GetAssets({ limit: 100, ...filters })).json();

        const resultsMap = new Map(data.map((asset) => [asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id }]));

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingAssets } = await (
              await ApiService.GetAssets({
                limit: 100,
                filters: { fields: { id: missingIds } },
              })
            ).json();

            for (const asset of missingAssets) {
              resultsMap.set(asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id });
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.accessor('rate', {
    header: 'Rate',
    cell: ({ getValue }) => {
      const value = getValue();
      const formatted = formatRateCellValue(value);
      if (!formatted) return <span className="text-secondary-text">-</span>;
      return <span>{formatted}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('recordedAt', {
    header: 'Created Date',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatDateWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      filterType: 'date-range',
    },
  }),
  columnHelper.display({
    id: 'createdTime',
    header: 'Created Time',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatTimeWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('location', {
    header: 'Location',
    cell: ({ getValue }) => {
      const location = getValue();
      return <span className="block truncate">{location?.name}</span>;
    },
    enableSorting: true,
    enableGrouping: true,
    enableColumnFilter: true,
    getGroupingValue: (row) => row.location?.name || 'None',
    meta: {
      cellClassName: 'max-w-[200px] overflow-hidden',
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data } = await (await ApiService.GetLocations({ limit: 500, ...filters })).json();

        const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingLocations } = await (
              await ApiService.GetLocations({
                limit: 500,
                filters: { fields: { id: missingIds } },
              })
            ).json();

            for (const location of missingLocations) {
              resultsMap.set(location.id, { label: location.name, value: location.id });
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.accessor('lat', {
    header: 'Latitude',
    cell: ({ getValue }) => {
      const lat = getValue();
      return <span>{lat}</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('lng', {
    header: 'Longitude',
    cell: ({ getValue }) => {
      const lng = getValue();
      return <span>{lng}</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('source', {
    header: 'Source',
    cell: ({ getValue, row }) => {
      const source = getValue();
      const form = row.original.form;

      return (
        <Badge
          variant={match(source as AssetLocationSource)
            .with(AssetLocationSource.Manual, () => 'success' as const)
            .with(AssetLocationSource.Form, () => {
              if (!form) return 'default' as const;
              return match(form.type)
                .with(FormType.InspectionForm, () => 'default' as const)
                .with(FormType.RepairForm, () => 'orange' as const)
                .with(FormType.ScheduleForm, () => 'violet' as const)
                .otherwise(() => 'default' as const);
            })
            .exhaustive()}
          className="max-w-[150px] capitalize">
          <span className="truncate">
            {match(source as AssetLocationSource)
              .with(AssetLocationSource.Manual, () => 'Manual' as const)
              .with(AssetLocationSource.Form, () => form?.name || ('Form' as const))
              .exhaustive()}
          </span>
        </Badge>
      );
    },
    enableColumnFilter: true,
    meta: {
      filterType: 'multi-select',
      getFilterOptionsFn: async () => {
        return Object.values(AssetLocationSource).map((source) => ({
          label: source.charAt(0).toUpperCase() + source.slice(1),
          value: source,
        }));
      },
    },
  }),
  columnHelper.display({
    id: 'category',
    header: 'Category',
    cell: ({ row }) => {
      const category = row.original.asset?.category;
      return category ? (
        <Badge variant="violet" size="sm">
          {category.name}
        </Badge>
      ) : (
        <span className="text-secondary-text">-</span>
      );
    },
    enableGrouping: true,
    enableColumnFilter: true,
    getGroupingValue: (row) => row.asset?.category?.name || 'None',
    meta: {
      columnGroup: ASSET_SECTION_LABEL,
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data } = await (await ApiService.GetAssetCategories({ limit: 1000, ...filters })).json();

        const resultsMap = new Map(data.map((assetCategory) => [assetCategory.id, { label: assetCategory.name, value: assetCategory.id }]));

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingAssetCategories } = await (
              await ApiService.GetAssetCategories({
                limit: 1000,
                filters: { fields: { id: missingIds } },
              })
            ).json();

            for (const assetCategory of missingAssetCategories) {
              resultsMap.set(assetCategory.id, { label: assetCategory.name, value: assetCategory.id });
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.display({
    id: 'type',
    header: 'Asset Type',
    cell: ({ row }) => {
      const type = row.original.asset?.type;
      return type ? <span>{type}</span> : <span className="text-secondary-text">-</span>;
    },
    enableGrouping: true,
    enableColumnFilter: true,
    getGroupingValue: (row) => row.asset?.type || 'None',
    meta: {
      columnGroup: ASSET_SECTION_LABEL,
      filterType: 'multi-select',
      getFilterOptionsFn: async () => {
        return Object.values(GearbeltAssetType).map((label) => ({
          label,
          value: label,
        }));
      },
    },
  }),
  columnHelper.display({
    id: 'manufacturer',
    header: 'Manufacturer',
    cell: ({ row }) => {
      const manufacturer = row.original.asset?.manufacturer;
      return manufacturer ? <span>{manufacturer}</span> : <span className="text-secondary-text">-</span>;
    },
    enableGrouping: true,
    enableColumnFilter: true,
    getGroupingValue: (row) => row.asset?.manufacturer || 'None',
    meta: {
      columnGroup: ASSET_SECTION_LABEL,
      filterType: 'async-multi-select',
      getFilterOptionsFn: async () => {
        const response = await (await ApiService.GetManufacturers({ limit: 500 })).json();
        return response.data.map((manufacturer) => ({
          label: manufacturer.make,
          value: manufacturer.make,
        }));
      },
    },
  }),
  columnHelper.display({
    id: 'createdBy',
    header: 'Created By',
    cell: ({ row }) => {
      const user = row.original.user;
      if (!user) return <span className="text-secondary-text">-</span>;

      const value = EntityRefUtil.createUserFullNameRef(user, '-');
      const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user?.firstName || '');
      return (
        <UserAvatarLabel
          value={value || '-'}
          operatedByFullInitials={initials(value || '-')}
          avatarUrl={user?.avatarUrl || ''}
          avatarBackgroundColorClass={avatarBackgroundColorClass}
        />
      );
    },
    meta: {
      cellClassName: 'w-[120px]',
    },
    enableSorting: false,
  }),
] as AccessorKeyColumnDef<GetAssetLocationsResponseDTO['data'][number]>[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<NonNullable<typeof columns>[number]['id']> | NonNullable<NonNullable<typeof columns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetAssetLocationsRequestDTO['filters']>['fields']>
> = {
  asset: 'assetId',
  location: 'locationId',
  source: 'source',
  category: 'categoryId',
  type: 'type',
  manufacturer: 'manufacturer',
  recordedAt: 'recordedAt',
};

export const GROUP_OPTIONS = [
  { label: 'None', accessor: '' },
  { label: 'Asset', accessor: 'asset' },
  { label: 'Location', accessor: 'location' },
  { label: 'Category', accessor: 'category' },
  { label: 'Asset Type', accessor: 'type' },
  { label: 'Manufacturer', accessor: 'manufacturer' },
] satisfies { label: string; accessor: string }[];
